import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";

function WhatsAppTemplatesPage() {
  const pageTitle = "WhatsApp - Templates";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const [pageLoaded, setPageLoaded] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTemplate, setDeleteTemplate] = useState(null);

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setSelectedTemplate(null);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setDeleteTemplate(null);
  };

  const handleDelete = () => {
    if (!deleteTemplate) {
      return;
    }

    setShowLoading(true);

    axios
      .delete(`/portal/wacc/businesses/templates/${deleteTemplate.reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setDeleteOpen(false);
          setDeleteTemplate(null);

          setTemplates((current) =>
            current.filter((template) => template.reference !== deleteTemplate.reference)
          );

          setSeverity("success");
          setErrMsg("Template deleted successfully.");
          setOpen(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("Unable to complete request.");
        setOpen(true);
      });
  };

  const renderPreview = () => {
    if (!selectedTemplate) {
      return null;
    }

    const components = selectedTemplate.components || [];
    const header = components.find((component) => component.type === "HEADER");
    const body = components.find((component) => component.type === "BODY");
    const footer = components.find((component) => component.type === "FOOTER");
    const buttons = components.find((component) => component.type === "BUTTONS");

    return (
      <MDBox
        sx={{
          backgroundColor: "#efeae2",
          borderRadius: 2,
          p: 3,
        }}
      >
        <MDBox
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: 2,
            p: 1.5,
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          {header && (
            <MDBox mb={1}>
              {header.format === "TEXT" && (
                <MDTypography variant="body2" fontWeight="bold">
                  {header.text}
                </MDTypography>
              )}

              {header.format && header.format !== "TEXT" && (
                <MDBox
                  sx={{
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    p: 2,
                    textAlign: "center",
                  }}
                >
                  <Icon sx={{ fontSize: 40 }}>
                    {header.format === "IMAGE"
                      ? "image"
                      : header.format === "VIDEO"
                      ? "videocam"
                      : header.format === "DOCUMENT"
                      ? "description"
                      : "attach_file"}
                  </Icon>

                  <MDTypography variant="caption" display="block" mt={0.5}>
                    {header.format}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          )}

          {body && (
            <MDTypography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.5,
              }}
            >
              {body.text || ""}
            </MDTypography>
          )}

          {footer && (
            <MDTypography variant="caption" color="text" display="block" mt={1}>
              {footer.text || ""}
            </MDTypography>
          )}

          {buttons?.buttons?.length > 0 && (
            <MDBox mt={1}>
              {buttons.buttons.map((button, index) => (
                <MDBox
                  key={index}
                  sx={{
                    borderTop: "1px solid #eeeeee",
                    pt: 1,
                    mt: 1,
                    textAlign: "center",
                  }}
                >
                  <MDTypography variant="button" color="info">
                    {button.text || button.type}
                  </MDTypography>
                </MDBox>
              ))}
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    );
  };

  const tableColumns = [
    { Header: "Template", accessor: "title", align: "left" },
    // { Header: "Code", accessor: "template_code", align: "left" },
    { Header: "Category", accessor: "category", align: "center" },
    { Header: "Language", accessor: "language", align: "center" },
    { Header: "Meta Status", accessor: "meta_status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        reference: row.reference,
        title: row.title,
        category: row.category,
        language: row.language,
        components: row.components,
        meta_status: (
          <MDTypography
            variant="button"
            color={
              row.meta_status === "APPROVED"
                ? "success"
                : row.meta_status === "REJECTED"
                ? "error"
                : "warning"
            }
          >
            {row.meta_status || "PENDING"}
          </MDTypography>
        ),
        actions: (
          <MDBox display="flex" justifyContent="flex-end" gap={1}>
            <Tooltip title="Preview">
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                iconOnly
                onClick={() => {
                  setSelectedTemplate(row);
                  setPreviewOpen(true);
                }}
              >
                <Icon fontSize="small">visibility</Icon>
              </MDButton>
            </Tooltip>

            <Tooltip title="Delete">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  setDeleteTemplate(row);
                  setDeleteOpen(true);
                }}
              >
                <Icon fontSize="small">delete</Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
      });

      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/portal/wacc/businesses/${ref}/templates.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setTemplates(buildRows(result.data.data.templates || []));
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("Unable to complete request.");
          setOpen(true);
        });
    };

    fetchData();
  }, [ref]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={previewOpen} onClose={handlePreviewClose}>
        <DialogTitle>{selectedTemplate?.title || "Template Preview"}</DialogTitle>

        <DialogContent>{renderPreview()}</DialogContent>

        <DialogActions>
          <Button color="dark" onClick={handlePreviewClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={handleDeleteClose} fullWidth maxWidth="xs">
        <DialogTitle>Delete Template</DialogTitle>

        <DialogContent>
          <MDTypography variant="body2">
            Are you sure you want to delete <strong>{deleteTemplate?.title}</strong>?
          </MDTypography>
        </DialogContent>

        <DialogActions>
          <Button color="dark" onClick={handleDeleteClose}>
            Cancel
          </Button>

          <MDButton variant="contained" color="error" onClick={handleDelete}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && (
        <MDBox mt={1} mb={5}>
          <MDBox mb={2} display="flex" justifyContent="space-between">
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                navigate(`/wacc/businesses/${ref}`);
              }}
            >
              Back
            </Button>

            <MDBox display="flex" gap={1}>
              <MDButton
                variant="outlined"
                color="dark"
                startIcon={<Icon>library_books</Icon>}
                onClick={() => {
                  navigate(`/wacc/businesses/${ref}/system-templates`);
                }}
                size="small"
              >
                System Templates
              </MDButton>

              <MDButton
                variant="contained"
                color="dark"
                startIcon={<Icon>add</Icon>}
                onClick={() => {
                  navigate(`/wacc/businesses/${ref}/templates/create`);
                }}
                size="small"
              >
                Create Template
              </MDButton>
            </MDBox>
          </MDBox>

          <Card>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="medium">
                  Message Templates
                </MDTypography>
              </MDBox>
            </MDBox>

            <DataTable
              table={{
                columns: tableColumns,
                rows: templates,
              }}
              canSearch
              isSorted
              entriesPerPage
              showTotalEntries
              buttons
              noEndBorder
            />
          </Card>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsAppTemplatesPage;
